import React, { ReactNode } from 'react';
import { Box } from '@chakra-ui/react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { ANIMATION_CONSTANTS } from '../../constants/animations';

interface AnimatedSectionProps {
  id: string;
  children: ReactNode;
  delay?: number;
  staggerDelay?: number;
  className?: string;
}

const AnimatedSection: React.FC<AnimatedSectionProps> = ({
  id,
  children,
  delay = 0,
  staggerDelay = 0.1,
  className,
}) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { 
    once: true, 
    margin: "-10% 0px -10% 0px",
    amount: 0.3 
  });
  const prefersReducedMotion = useReducedMotion();

  // Medical-grade animation variants - professional, subtle, confident
  const containerVariants = {
    hidden: { 
      opacity: 0,
      y: 40,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0 : ANIMATION_CONSTANTS.DURATIONS.SLOW / 1000,
        ease: ANIMATION_CONSTANTS.EASING.PRIMARY,
        delay: prefersReducedMotion ? 0 : delay,
        staggerChildren: prefersReducedMotion ? 0 : staggerDelay,
      },
    },
  };

  const childVariants = {
    hidden: { 
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0 : ANIMATION_CONSTANTS.DURATIONS.NORMAL / 1000,
        ease: ANIMATION_CONSTANTS.EASING.PRIMARY,
      },
    },
  };

  // If user prefers reduced motion, render without animations
  if (prefersReducedMotion) {
    return (
      <Box id={id} ref={ref} className={className}>
        {children}
      </Box>
    );
  }

  return (
    <motion.div
      ref={ref}
      id={id}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      className={className}
    >
      <motion.div variants={childVariants}>
        {children}
      </motion.div>
    </motion.div>
  );
};

export default AnimatedSection; 